/**
 * Передать счёт другому сотруднику.
 *
 * Официант заканчивает смену, а гости сидят и заказывают дальше. Стол
 * переходит сменщику вместе с ответственностью: он доводит гостей
 * до оплаты, и выручка достаётся ему.
 *
 * Показываем только тех, кто СЕЙЧАС на смене: отдать стол человеку,
 * которого нет в заведении, значит потерять его до утра.
 */
import { useEffect, useState } from 'react';
import { BASE } from '../api';

type Mate = { id: string; name: string; since: string };

export function ReassignDialog({ token, tableName, onDone, onCancel }: {
  token: string;
  tableName: string;
  onDone: (toUserId: string, toName: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [mates, setMates] = useState<Mate[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    void (async () => {
      try {
        const r = await fetch(`${BASE}/open-orders/shift-staff`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = await r.json() as { rows: Mate[] };
        setMates(d.rows);
      } catch {
        setErr('Не удалось получить список — нужна связь');
        setMates([]);
      }
    })();
  }, [token]);

  const hhmm = (iso: string) =>
    new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="dialog-backdrop center" onClick={onCancel}>
      <div className="dialog" onClick={(e) => e.stopPropagation()}>
        <h2>Передать стол {tableName}</h2>
        <p className="hint">
          Сменщик продолжит вести гостей, и выручка по столу перейдёт ему.
          Позиции, скидка и отправленные на кухню блюда останутся как есть.
        </p>

        {mates === null && <p className="hint">Смотрим, кто на смене…</p>}
        {err && <p className="error">{err}</p>}

        {mates?.length === 0 && !err && (
          <p className="hint">
            На смене больше никого. Стол можно отложить — старший заберёт
            его через «Открытые столы».
          </p>
        )}

        <div className="mates">
          {(mates ?? []).map((m) => (
            <button key={m.id} className="mate" disabled={busy}
              onClick={() => {
                setBusy(true);
                void onDone(m.id, m.name).catch(() => {
                  setErr('Не получилось передать. Попробуйте ещё раз');
                  setBusy(false);
                });
              }}>
              <b>{m.name}</b>
              <span>на смене с {hhmm(m.since)}</span>
            </button>
          ))}
        </div>

        <div className="dialog-actions">
          <button className="btn" onClick={onCancel} disabled={busy}>Отмена</button>
        </div>
      </div>
    </div>
  );
}
